import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertTriangle, ExternalLink, KeyRound, Plus, Save, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatDateTime, formatNumber } from '../lib/format';
import {
  DIMENSIONS,
  PILLARS,
  type AccessRow,
  type AccessState,
  type ConfigProblem,
  type Dimension,
  type ExecutiveConfig,
  type SettingsRead,
} from '../lib/executive';
import { Badge, Button, Card, CardHeader, Checkbox, EmptyState, ErrorState, Field, Input, LoadingBlock, Select, Table, Td, Th, Tr, useToast } from './ui';

/**
 * Settings › Executive Dashboard (CR-008 §4.3, §3.1 item 10) — an Administrator's: who may open
 * dashboard.viganium.net, and its settings. Granting and revoking are form pages of their own; this lists.
 */
export function ExecutiveDashboardPanel() {
  const { user } = useAuth();
  const { t } = useI18n();
  return (
    <div className="space-y-5">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 max-w-3xl space-y-1.5">
            <p className="text-title text-[var(--text-primary)]">{t('exec.title')}</p>
            <p className="text-body-sm text-[var(--text-secondary)]">{t('exec.intro')}</p>
            <p className="text-body-sm text-muted">{t('exec.rule')}</p>
          </div>
          {user?.executiveDashboard?.url && (
            <a href={user.executiveDashboard.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-body-sm text-primary-600 hover:underline dark:text-primary-400">
              <ExternalLink className="h-4 w-4" aria-hidden />
              {t('exec.open')}
            </a>
          )}
        </div>
      </Card>
      <AccessList />
      <SettingsForm />
    </div>
  );
}

const STATE_TONE: Record<AccessState, 'success' | 'neutral' | 'warning'> = { GRANTED: 'success', NOT_GRANTED: 'neutral', ENDED: 'warning' };

function AccessList() {
  const { t, locale, localized } = useI18n();
  const [q, setQ] = useState('');
  const [state, setState] = useState<'ALL' | AccessState>('ALL');
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['executive-access', q, state],
    queryFn: () => api.get<AccessRow[]>('/dashboard-access', { q: q.trim() || undefined, state }),
  });
  return (
    <Card padded={false}>
      <div className="p-5 pb-3">
        <CardHeader title={t('exec.accessTitle')} subtitle={t('exec.accessSubtitle')} className="mb-3" />
        <div className="flex flex-wrap gap-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('exec.searchPeople')} className="!w-64" aria-label={t('exec.searchPeople')} />
          <Select value={state} onChange={(e) => setState(e.target.value as 'ALL' | AccessState)} className="!w-auto" aria-label={t('common.status')}>
            <option value="ALL">{t('common.all')}</option>
            <option value="GRANTED">{t('exec.state.GRANTED')}</option>
            <option value="NOT_GRANTED">{t('exec.state.NOT_GRANTED')}</option>
            <option value="ENDED">{t('exec.state.ENDED')}</option>
          </Select>
        </div>
      </div>
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data?.length ? (
        <EmptyState title={t('exec.noPeople')} hint={t('exec.noPeopleHint')} />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('common.employee')}</Th>
              <Th>{t('exec.col.role')}</Th>
              <Th>{t('exec.col.access')}</Th>
              <Th>{t('exec.col.grantedBy')}</Th>
              <Th>{t('exec.col.lastSignIn')}</Th>
              <Th align="end">{t('exec.col.sessions')}</Th>
              <Th align="end">{t('common.actions')}</Th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <Tr key={row.user.id}>
                <Td>
                  <div className="min-w-0">
                    <p className="font-medium text-[var(--text-primary)]">{localized(row.user)}</p>
                    <p className="text-caption text-muted" dir="ltr">
                      {row.user.email}
                    </p>
                  </div>
                </Td>
                <Td>
                  <div className="flex flex-wrap gap-1">
                    {row.roles.map((r) => (
                      <Badge key={r.code} tone={r.code === 'ADMIN' ? 'brand' : 'neutral'}>
                        {locale === 'ar' ? r.nameAr || r.name : r.name}
                      </Badge>
                    ))}
                    {!row.user.isActive && <Badge tone="warning">{t('exec.inactive')}</Badge>}
                  </div>
                </Td>
                <Td>
                  <div className="space-y-1">
                    <Badge tone={STATE_TONE[row.access.state]}>{t(`exec.state.${row.access.state}`)}</Badge>
                    {row.stale && (
                      <p className="flex items-center gap-1 text-caption text-warning-fg dark:text-warning-dark">
                        <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                        {t('exec.stale')}
                      </p>
                    )}
                    {row.access.state === 'ENDED' && row.access.endedBecause && (
                      <p className="text-caption text-muted">{t(`exec.event.${row.access.endedBecause}`)}</p>
                    )}
                  </div>
                </Td>
                <Td>
                  {row.access.grantedAt ? (
                    <span className="text-body-sm">
                      {row.access.grantedBy ? localized(row.access.grantedBy) : '—'}
                      <span className="block text-caption text-muted">{formatDate(row.access.grantedAt, locale)}</span>
                    </span>
                  ) : (
                    '—'
                  )}
                </Td>
                <Td>{row.lastDashboardSignInAt ? formatDateTime(row.lastDashboardSignInAt, locale) : '—'}</Td>
                <Td align="end" className="tabular">
                  {formatNumber(row.activeDashboardSessions, locale)}
                </Td>
                <Td align="end">
                  <div className="flex justify-end gap-2">
                    {row.canGrant && (
                      <Link to={`/settings/executive-access/${row.user.id}/grant`} className="inline-flex items-center gap-1 text-body-sm text-primary-600 hover:underline dark:text-primary-400">
                        <KeyRound className="h-4 w-4" aria-hidden />
                        {t('exec.grant')}
                      </Link>
                    )}
                    {row.access.state === 'GRANTED' && (
                      <Link to={`/settings/executive-access/${row.user.id}/revoke`} className="text-body-sm text-error-fg hover:underline dark:text-error-dark">
                        {t('exec.revoke')}
                      </Link>
                    )}
                  </div>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

type Draft = ExecutiveConfig & { effectiveFrom: string };

function SettingsForm() {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['executive-settings'], queryFn: () => api.get<SettingsRead>('/dashboard-settings') });
  const { data: departments } = useQuery({
    queryKey: ['departments', 'options'],
    queryFn: () => api.get<Array<{ id: string; name: string; nameAr: string | null }>>('/departments/options'),
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [acknowledge, setAcknowledge] = useState(false);
  const [problems, setProblems] = useState<ConfigProblem[]>([]);
  const today = new Date().toISOString().slice(0, 10);
  useEffect(() => {
    if (data) setDraft({ ...structuredClone(data.latest.config), effectiveFrom: today });
    setProblems([]);
    setAcknowledge(false);
  }, [data, today]);

  const save = useMutation({
    mutationFn: () => {
      const { effectiveFrom, ...config } = draft!;
      return api.put<{ version: number; effectiveFrom: string }>('/dashboard-settings', { config, effectiveFrom, acknowledgeAiNotice: acknowledge || undefined });
    },
    onSuccess: (r) => {
      toast.success(t('exec.saved', { v: r.version, day: r.effectiveFrom }));
      void queryClient.invalidateQueries({ queryKey: ['executive-settings'] });
    },
    onError: (e: Error) => {
      setProblems(e instanceof ApiError ? ((e.details as { problems?: ConfigProblem[] })?.problems ?? []) : []);
      toast.error(e.message);
    },
  });

  const sums = useMemo(() => {
    if (!draft) return { scoring: 0, health: 0 };
    return {
      scoring: DIMENSIONS.reduce((s, d) => s + (draft.scoring.enabled[d] ? Number(draft.scoring.weights[d]) || 0 : 0), 0),
      health: PILLARS.reduce((s, p) => s + (Number(draft.health.weights[p]) || 0), 0),
    };
  }, [draft]);

  if (isLoading || (!draft && !error)) return <LoadingBlock />;
  if (error || !data || !draft) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const problemFor = (prefix: string) => problems.filter((p) => p.field === prefix || p.field.startsWith(`${prefix}.`));
  const reasonText = (p: ConfigProblem) => {
    const key = `exec.problem.${p.reason}` as TranslationKey;
    return `${p.field}: ${t(key) === key ? p.reason : t(key)}`;
  };
  const num = (v: string) => (v === '' ? ('' as unknown as number) : Number(v));
  const set = (fn: (d: Draft) => void) => {
    const next = structuredClone(draft);
    fn(next);
    setDraft(next);
  };
  const server = data.server.ai;
  const turningAiOn = draft.ai.enabled && !data.latest.config.ai.enabled;

  return (
    <Card>
      <CardHeader
        title={t('exec.settingsTitle')}
        subtitle={t('exec.settingsSubtitle', { v: data.current.version, latest: data.latest.version })}
        action={
          <Button variant="primary" icon={<Save className="h-4 w-4" aria-hidden />} loading={save.isPending} disabled={turningAiOn && !acknowledge} onClick={() => save.mutate()}>
            {t('common.save')}
          </Button>
        }
      />
      {problems.length > 0 && (
        <ul className="mb-4 space-y-1 rounded-md border border-error/30 bg-error/10 p-3 text-body-sm text-error-fg dark:text-error-dark">
          {problems.map((p) => (
            <li key={`${p.field}:${p.reason}`}>{reasonText(p)}</li>
          ))}
        </ul>
      )}

      <div className="space-y-6">
        <section>
          <h4 className="mb-1 text-body font-medium text-[var(--text-primary)]">{t('exec.scoring')}</h4>
          <p className="mb-3 text-caption text-faint">{t('exec.scoringHint')}</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {DIMENSIONS.map((d) => (
              <div key={d} className="space-y-1.5">
                <Field label={t(`exec.dim.${d}`)}>
                  <Input type="number" min={0} max={100} step={1} value={String(draft.scoring.weights[d] ?? '')} onChange={(e) => set((x) => (x.scoring.weights[d] = num(e.target.value)))} />
                </Field>
                <Checkbox checked={draft.scoring.enabled[d]} onChange={(e) => set((x) => (x.scoring.enabled[d] = e.target.checked))} label={t('exec.enabled')} />
              </div>
            ))}
          </div>
          <p className={clsx('mt-2 text-body-sm tabular', sums.scoring === 100 ? 'text-success-fg dark:text-success' : 'text-error-fg dark:text-error-dark')}>
            {t('exec.sum', { n: formatNumber(sums.scoring, locale) })}
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <Field label={t('exec.minEvents')} hint={t('exec.minEventsHint')}>
              <Input type="number" min={1} max={50} value={String(draft.scoring.minEvidenceEvents)} onChange={(e) => set((x) => (x.scoring.minEvidenceEvents = num(e.target.value)))} />
            </Field>
            <Field label={t('exec.minWeight')} hint={t('exec.minWeightHint')}>
              <Input type="number" min={10} max={100} value={String(draft.scoring.minMeasurableWeightPercent)} onChange={(e) => set((x) => (x.scoring.minMeasurableWeightPercent = num(e.target.value)))} />
            </Field>
            <Field label={t('exec.reviewHours')} hint={t('exec.reviewHoursHint')}>
              <Input type="number" min={1} max={720} value={String(draft.scoring.reviewOverdueHours)} onChange={(e) => set((x) => (x.scoring.reviewOverdueHours = num(e.target.value)))} />
            </Field>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {(['excellent', 'veryGood', 'good'] as const).map((k) => (
              <Field key={k} label={t(`exec.label.${k}`)}>
                <Input type="number" min={1} max={100} value={String(draft.scoring.labels[k])} onChange={(e) => set((x) => (x.scoring.labels[k] = num(e.target.value)))} />
              </Field>
            ))}
          </div>
        </section>

        <section>
          <div className="mb-2 flex items-center justify-between gap-3">
            <h4 className="text-body font-medium text-[var(--text-primary)]">{t('exec.overrides')}</h4>
            <Button
              size="sm"
              icon={<Plus className="h-4 w-4" aria-hidden />}
              disabled={!departments?.length}
              onClick={() =>
                set((x) =>
                  x.scoring.departmentOverrides.push({
                    departmentId: departments!.find((d) => !x.scoring.departmentOverrides.some((o) => o.departmentId === d.id))?.id ?? departments![0].id,
                    weights: { ...x.scoring.weights },
                    enabled: { ...x.scoring.enabled },
                  }),
                )
              }
            >
              {t('exec.addOverride')}
            </Button>
          </div>
          <p className="mb-3 text-caption text-faint">{t('exec.overridesHint')}</p>
          {draft.scoring.departmentOverrides.map((o, i) => {
            const total = DIMENSIONS.reduce((s, d) => s + (o.enabled[d] ? Number(o.weights[d]) || 0 : 0), 0);
            return (
              <div key={i} className="mb-3 rounded-md border hairline p-3">
                <div className="mb-2 flex flex-wrap items-end gap-3">
                  <Field label={t('common.department')}>
                    <Select value={o.departmentId} onChange={(e) => set((x) => (x.scoring.departmentOverrides[i].departmentId = e.target.value))}>
                      {departments?.map((d) => (
                        <option key={d.id} value={d.id}>
                          {localized(d)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Button size="sm" variant="ghost" icon={<Trash2 className="h-4 w-4" aria-hidden />} onClick={() => set((x) => x.scoring.departmentOverrides.splice(i, 1))}>
                    {t('common.delete')}
                  </Button>
                  <span className={clsx('text-body-sm tabular', total === 100 ? 'text-success-fg dark:text-success' : 'text-error-fg dark:text-error-dark')}>{t('exec.sum', { n: formatNumber(total, locale) })}</span>
                </div>
                <div className="grid gap-2 sm:grid-cols-5">
                  {DIMENSIONS.map((d: Dimension) => (
                    <div key={d} className="space-y-1">
                      <Field label={t(`exec.dim.${d}`)}>
                        <Input type="number" min={0} max={100} value={String(o.weights[d] ?? '')} onChange={(e) => set((x) => (x.scoring.departmentOverrides[i].weights[d] = num(e.target.value)))} />
                      </Field>
                      <Checkbox checked={o.enabled[d]} onChange={(e) => set((x) => (x.scoring.departmentOverrides[i].enabled[d] = e.target.checked))} label={t('exec.enabled')} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </section>

        <section>
          <h4 className="mb-1 text-body font-medium text-[var(--text-primary)]">{t('exec.health')}</h4>
          <p className="mb-3 text-caption text-faint">{t('exec.healthHint')}</p>
          <div className="grid gap-3 sm:grid-cols-4">
            {PILLARS.map((p) => (
              <Field key={p} label={t(`exec.pillar.${p}`)}>
                <Input type="number" min={0} max={100} value={String(draft.health.weights[p])} onChange={(e) => set((x) => (x.health.weights[p] = num(e.target.value)))} />
              </Field>
            ))}
          </div>
          <p className={clsx('mt-2 text-body-sm tabular', sums.health === 100 ? 'text-success-fg dark:text-success' : 'text-error-fg dark:text-error-dark')}>{t('exec.sum', { n: formatNumber(sums.health, locale) })}</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-4">
            {(['minMeasurableWeightPercent', 'minScoredEmployees', 'minTickets', 'minDatedTasks'] as const).map((k) => (
              <Field key={k} label={t(`exec.health.${k}`)}>
                <Input type="number" min={1} value={String(draft.health[k])} onChange={(e) => set((x) => (x.health[k] = num(e.target.value)))} />
              </Field>
            ))}
          </div>
        </section>

        <section>
          <h4 className="mb-3 text-body font-medium text-[var(--text-primary)]">{t('exec.risks')}</h4>
          <div className="grid gap-3 sm:grid-cols-3">
            {(['blockedQTaskDays', 'stalledWorkflowDays', 'contractExpiryDays', 'overdueReceivableSharePercent', 'expenseSpikePercent', 'receivableConcentrationPercent'] as const).map((k) => (
              <Field key={k} label={t(`exec.risk.${k}`)}>
                <Input type="number" min={1} value={String(draft.risks[k])} onChange={(e) => set((x) => (x.risks[k] = num(e.target.value)))} />
              </Field>
            ))}
          </div>
        </section>

        <section>
          <h4 className="mb-1 text-body font-medium text-[var(--text-primary)]">{t('exec.ai')}</h4>
          <div className="mb-3 rounded-md border border-warning/40 bg-warning/10 p-3 text-body-sm text-[var(--text-secondary)]">
            <p className="font-medium text-[var(--text-primary)]">{t('exec.aiNoticeTitle')}</p>
            <p className="mt-1">{t('exec.aiNotice')}</p>
            <p className="mt-1 text-muted">
              {server.enabled ? t('exec.aiServerOn') : t('exec.aiServerOff')} · {server.configured ? t('exec.aiKeySet') : t('exec.aiKeyMissing')} ·{' '}
              {t('exec.aiBudgetServer', { n: formatNumber(server.monthlyTokenBudget, locale) })}
            </p>
          </div>
          <div className="space-y-3">
            <Checkbox checked={draft.ai.enabled} onChange={(e) => set((x) => (x.ai.enabled = e.target.checked))} label={t('exec.aiEnabled')} />
            {turningAiOn && <Checkbox checked={acknowledge} onChange={(e) => setAcknowledge(e.target.checked)} label={t('exec.aiAcknowledge')} />}
            {data.latest.config.ai.consent.confirmedAt && (
              <p className="text-caption text-muted">{t('exec.aiConsentAt', { at: formatDateTime(data.latest.config.ai.consent.confirmedAt, locale) })}</p>
            )}
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label={t('exec.aiModel')}>
                <Select value={draft.ai.model ?? ''} onChange={(e) => set((x) => (x.ai.model = e.target.value || null))}>
                  <option value="">{t('exec.aiModelDefault', { model: server.defaultModel ?? '—' })}</option>
                  {server.models.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('exec.aiBudget')} hint={t('exec.aiBudgetHint')}>
                <Input type="number" min={1000} value={draft.ai.monthlyTokenBudget === null ? '' : String(draft.ai.monthlyTokenBudget)} onChange={(e) => set((x) => (x.ai.monthlyTokenBudget = e.target.value === '' ? null : Number(e.target.value)))} />
              </Field>
              <Field label={t('exec.aiPriceIn')} hint={t('exec.aiPriceHint')}>
                <Input type="number" min={0} step="0.01" value={draft.ai.priceEstimate.inputPerMillion === null ? '' : String(draft.ai.priceEstimate.inputPerMillion)} onChange={(e) => set((x) => (x.ai.priceEstimate.inputPerMillion = e.target.value === '' ? null : Number(e.target.value)))} />
              </Field>
              <Field label={t('exec.aiPriceOut')}>
                <Input type="number" min={0} step="0.01" value={draft.ai.priceEstimate.outputPerMillion === null ? '' : String(draft.ai.priceEstimate.outputPerMillion)} onChange={(e) => set((x) => (x.ai.priceEstimate.outputPerMillion = e.target.value === '' ? null : Number(e.target.value)))} />
              </Field>
              <Field label={t('exec.aiPriceAsOf')}>
                <Input type="date" dir="ltr" value={draft.ai.priceEstimate.asOf ?? ''} onChange={(e) => set((x) => (x.ai.priceEstimate.asOf = e.target.value || null))} />
              </Field>
            </div>
            <div className="flex flex-wrap gap-4">
              {(['daily', 'weekly', 'monthly', 'employees'] as const).map((k) => (
                <Checkbox key={k} checked={draft.ai.reports[k]} onChange={(e) => set((x) => (x.ai.reports[k] = e.target.checked))} label={t(`exec.aiReport.${k}`)} />
              ))}
            </div>
            {problemFor('ai').length > 0 && <p className="text-caption text-error-fg dark:text-error-dark">{problemFor('ai').map(reasonText).join(' · ')}</p>}
          </div>
        </section>

        <section className="grid gap-3 sm:grid-cols-3">
          <Field label={t('exec.dailyAt')} hint={t('exec.dailyAtHint')}>
            <Input type="time" dir="ltr" value={draft.schedule.dailyAt} onChange={(e) => set((x) => (x.schedule.dailyAt = e.target.value))} />
          </Field>
          <Field label={t('exec.monthlyAt')}>
            <Input type="time" dir="ltr" value={draft.schedule.monthlyAt} onChange={(e) => set((x) => (x.schedule.monthlyAt = e.target.value))} />
          </Field>
          <Field label={t('exec.settleDays')} hint={t('exec.settleDaysHint')}>
            <Input type="number" min={1} max={60} value={String(draft.schedule.settleDays)} onChange={(e) => set((x) => (x.schedule.settleDays = num(e.target.value)))} />
          </Field>
          <Checkbox checked={draft.schedule.weekly} onChange={(e) => set((x) => (x.schedule.weekly = e.target.checked))} label={t('exec.weekly')} />
          <Field label={t('exec.retainSnapshots')} hint={t('exec.retainHint')}>
            <Input type="number" min={0} max={240} value={String(draft.retention.snapshotMonths)} onChange={(e) => set((x) => (x.retention.snapshotMonths = num(e.target.value)))} />
          </Field>
          <Field label={t('exec.retainExports')}>
            <Input type="number" min={0} max={240} value={String(draft.retention.exportLogMonths)} onChange={(e) => set((x) => (x.retention.exportLogMonths = num(e.target.value)))} />
          </Field>
          <Field label={t('exec.effectiveFrom')} hint={t('exec.effectiveFromHint')}>
            <Input type="date" dir="ltr" min={today} value={draft.effectiveFrom} onChange={(e) => set((x) => (x.effectiveFrom = e.target.value))} />
          </Field>
        </section>

        {data.history.length > 0 && (
          <section>
            <h4 className="mb-2 text-body font-medium text-[var(--text-primary)]">{t('exec.history')}</h4>
            <ul className="space-y-1 text-body-sm text-muted">
              {data.history.map((h) => (
                <li key={h.version}>
                  {t('exec.historyRow', {
                    v: h.version,
                    day: h.effectiveFrom ?? '—',
                    who: h.createdBy ? localized(h.createdBy) : '—',
                    at: h.createdAt ? formatDateTime(h.createdAt, locale) : '—',
                  })}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </Card>
  );
}
